/**
 * ErrorBoundary — filet de sécurité global qui remplace l'écran blanc/noir
 * par un message d'erreur lisible + un bouton de réinitialisation.
 *
 * Spec iter101 (2026-08-22, bug Fawzi) : quand un composant en aval crash
 * (ex : matrice de permissions vide, état utilisateur anormal, etc.), React
 * démonte tout et affiche un écran vide. Ce boundary garantit qu'on voit
 * toujours SOMETHING + un bouton de déconnexion qui purge le token, plus le
 * détail de l'erreur pour aider au debug.
 */
import { Component } from "react";
import { Button } from "@/components/ui/button";
import { errText } from "@/lib/api";

export default class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { error: null, info: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // Log to browser console so F12 always shows the crash, even in prod.
    // eslint-disable-next-line no-console
    console.error("[Le Desk] ErrorBoundary caught:", error, info);
    this.setState({ info });
    try { import("@/lib/api").then(({ default: api }) => api.post("/health/client-error", { route: window.location.pathname, message: String(error?.message || error).slice(0, 500), stack: String(info?.componentStack || error?.stack || "").slice(0, 3000) }).catch(() => {})); } catch { /* silencieux */ }

    // Spec iter113 (2026-02) — remontée AUTOMATIQUE au backend pour envoyer
    // un email au Premier Admin (dedup 1h côté serveur par fingerprint).
    // Best-effort : n'échoue jamais silencieusement pour l'utilisateur.
    try {
      const backend = process.env.REACT_APP_BACKEND_URL;
      if (backend) {
        let userEmail = null; let userId = null;
        try {
          // JWT stocké dans localStorage sous `ledesk_token`. On décode le
          // payload (base64url) pour récupérer sub + email sans dépendance
          // externe. Best-effort — si le token est absent ou corrompu, on
          // envoie le crash en anonyme.
          const token = sessionStorage.getItem("ledesk_token");
          if (token && token.split(".").length === 3) {
            const payload64 = token.split(".")[1]
              .replace(/-/g, "+").replace(/_/g, "/");
            const jsonStr = atob(payload64);
            const claims = JSON.parse(jsonStr);
            userEmail = claims?.email || null;
            userId = claims?.sub || null;
          }
        } catch { /* ignore — send as anonymous */ }
        const payload = {
          message: (error?.message || String(error || "")).slice(0, 2000),
          stack: (error?.stack || "").slice(0, 20000),
          component_stack: (info?.componentStack || "").slice(0, 10000),
          url: window.location.href.slice(0, 1000),
          user_id: userId,
          user_email: userEmail,
          user_agent: (navigator.userAgent || "").slice(0, 500),
        };
        // fire-and-forget (keepalive pour survivre à un reload immédiat)
        fetch(`${backend}/api/errors/frontend`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
          keepalive: true,
        }).catch(() => { /* silent — never crash the boundary itself */ });
      }
    } catch { /* ignore any reporting failure */ }

    // Spec iter101 rev — auto-recovery pour la signature typique
    // « NotFoundError: … removeChild … » causée par les extensions de
    // traduction (Google Translate, Edge Translate, etc.) qui insèrent
    // des <font> dans le DOM et cassent l'unmount React.
    // Cf. bug Fawzi 2026-08-23 : la stack matchait ce pattern exact.
    // On force un reload UNE seule fois pour repartir sur un DOM propre —
    // les meta translate=no + notranslate posés dans index.html empêchent
    // la ré-injection au 2ᵉ chargement dans la majorité des cas.
    const msg = error?.message || String(error || "");
    const name = error?.name || "";
    const isTranslateCrash =
      name === "NotFoundError" && /removeChild|insertBefore/i.test(msg);
    if (isTranslateCrash) {
      try {
        const KEY = "ledesk_translate_recover_at";
        const last = Number(sessionStorage.getItem(KEY) || 0);
        const now = Date.now();
        // Anti-boucle : on ne se recharge qu'une fois toutes les 60s max.
        if (now - last > 60_000) {
          sessionStorage.setItem(KEY, String(now));
          // Petit délai pour laisser le console.error partir avant reload.
          setTimeout(() => window.location.reload(), 250);
        }
      } catch { /* ignore */ }
    }
  }

  handleReset = () => {
    try { sessionStorage.removeItem("ledesk_token"); } catch { /* ignore */ }
    try { sessionStorage.clear(); } catch { /* ignore */ }
    window.location.href = "/connexion";
  };

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (!this.state.error) return this.props.children;
    const msg = errText(this.state.error, String(this.state.error));
    const stack = this.state.error?.stack || "";
    const componentStack = this.state.info?.componentStack || "";

    return (
      <div
        data-testid="global-error-boundary"
        style={{
          minHeight: "100vh",
          backgroundColor: "#1B1B1B",
          color: "#EFEFEF",
          fontFamily: "Tahoma, Geneva, sans-serif",
          padding: "48px 32px",
          overflowY: "auto",
        }}
      >
        <div style={{ maxWidth: 780, margin: "0 auto" }}>
          <div style={{ fontSize: 12, letterSpacing: 2, opacity: 0.6, marginBottom: 16 }}>
            LE DESK
          </div>
          <h1 style={{ fontSize: 28, fontWeight: 600, marginBottom: 12 }}>
            Un problème inattendu s'est produit
          </h1>
          <p style={{ fontSize: 14, opacity: 0.8, marginBottom: 24 }}>
            L'application a rencontré une erreur et n'a pas pu s'afficher.
            Vous pouvez recharger la page ou vous déconnecter puis vous reconnecter.
            Si le problème persiste, contactez un administrateur avec le détail ci-dessous.
          </p>

          <div style={{ display: "flex", gap: 12, marginBottom: 32 }}>
            <Button
              onClick={this.handleReload}
              data-testid="error-boundary-reload"
              style={{ backgroundColor: "#EFEFEF", color: "#1B1B1B" }}
            >
              Recharger la page
            </Button>
            <Button
              onClick={this.handleReset}
              variant="outline"
              data-testid="error-boundary-logout"
              style={{ borderColor: "#3A3A3A", color: "#EFEFEF", background: "transparent" }}
            >
              Se déconnecter
            </Button>
          </div>

          <details style={{ backgroundColor: "#232323", borderRadius: 8, padding: 16, border: "1px solid #333" }}>
            <summary style={{ cursor: "pointer", fontSize: 13, opacity: 0.85 }}>
              Détail technique (à envoyer à l'admin)
            </summary>
            <div style={{ fontSize: 12, marginTop: 12, whiteSpace: "pre-wrap", wordBreak: "break-word", opacity: 0.85 }}>
              <div><strong>Message :</strong> {msg}</div>
              {stack && (
                <div style={{ marginTop: 12 }}>
                  <strong>Stack :</strong>
                  <pre style={{ marginTop: 6, fontSize: 11, opacity: 0.75 }}>{stack}</pre>
                </div>
              )}
              {componentStack && (
                <div style={{ marginTop: 12 }}>
                  <strong>Composants :</strong>
                  <pre style={{ marginTop: 6, fontSize: 11, opacity: 0.75 }}>{componentStack}</pre>
                </div>
              )}
            </div>
          </details>
        </div>
      </div>
    );
  }
}
